<template>
  <div
    class="bg-background border rounded-lg shadow-lg p-4 mb-2 min-w-[300px] max-w-[400px] animate-in fade-in slide-in-from-right-4"
    :class="{
      'bg-green-50 border-green-200': toast.type === 'success',
      'bg-red-50 border-red-200': toast.type === 'error',
      'bg-blue-50 border-blue-200': toast.type === 'info',
      'bg-yellow-50 border-yellow-200': toast.type === 'warning'
    }"
  >
    <div class="flex items-start justify-between">
      <p class="text-sm font-medium">
        {{ toast.title }}
      </p>
      <button
        class="ml-4 text-gray-400 hover:text-gray-500"
        @click="$emit('close')"
      >
        <span class="sr-only">Close</span>
        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Toast {
  id: number
  title: string
  description?: string
  duration: number
  type: 'default' | 'success' | 'error' | 'warning' | 'info'
  visible: boolean
}

defineProps<{
  toast: Toast
}>()

defineEmits<{
  (e: 'close'): void
}>()
</script>

<style scoped>
.toast-container {
  background-color: white;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-bottom: 0.75rem;
  width: 350px;
  max-width: 100%;
  pointer-events: auto;
  overflow: hidden;
  opacity: 0;
  transform: translateY(1rem);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.toast-visible {
  opacity: 1;
  transform: translateY(0);
}

.toast-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.toast-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
}

.toast-close {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #718096;
}

.toast-body {
  padding: 0.75rem 1rem;
  color: #4a5568;
}

/* Toast Types */
.toast-default {
  border-left: 4px solid #3182ce;
}

.toast-success {
  border-left: 4px solid #38a169;
}

.toast-error {
  border-left: 4px solid #e53e3e;
}

.toast-warning {
  border-left: 4px solid #d69e2e;
}

.toast-info {
  border-left: 4px solid #3182ce;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .toast-container {
    background-color: #1e1e2e;
    color: #e2e8f0;
  }
  
  .toast-header {
    border-bottom-color: #2d3748;
  }
  
  .toast-close {
    color: #a0aec0;
  }
  
  .toast-body {
    color: #cbd5e0;
  }
}
</style> 